/* eslint-disable @typescript-eslint/no-explicit-any */

export type MiniSearchBarActivePanel = 'dates' | 'rooms' | 'rate' | null;

/** LLM date metadata from BE response */
export interface LlmDateInfo {
  earliest?: string; // e.g. "2026-03-27"
  latest?: string; // e.g. "2026-03-29"
  length?: number; // number of nights
  type?: 'EXACT' | 'FLEXIBLE' | string;
}

/** LLM metadata from BE response */
export interface LlmMetadata {
  isLlmEngaged?: boolean;
  llmInfo?: {
    queryRewrite?: string;
    date?: LlmDateInfo;
    location?: any;
    price?: any;
    callTimes?: any;
  };
  payload?: {
    query?: string;
  };
  /**
   * True when the TIP SSE payload contains `isFlexDate: true`.
   * Signals that the date returned is a flexible date (month + nights),
   * not a fixed check-in/check-out range.
   */
  isFlexDate?: boolean;
}

export interface MiniSearchBarProps {
  /** AEM / CMS model — passed through to inner NG molecules */
  model?: any;
  /** Current locale string, e.g. "en-US" */
  currentLocale?: string;
  /** Session data forwarded to datepicker inventory calls */
  sessionData?: any;
  /** Headers data for GraphQL calls */
  headersData?: object;
  /** Fired when the user clicks "Find Hotels" / Apply inside any dropdown */
  onSearch?: (searchParams: MiniSearchBarSearchParams) => void;
  /** Additional CSS class applied to the outer wrapper */
  className?: string;
  /** When true, renders skeleton shimmer placeholders over the pill segments */
  isLoading?: boolean;
  /** LLM metadata from BE response — used to populate dates from natural language query */
  llmMetadata?: LlmMetadata;
  /**
   * Callback for NLS search integration.
   * When provided, the MiniSearchBar will trigger NLS searches when filters change.
   * The payload.query (from the SSE event) is used as the base query, with
   * llmInfo.queryRewrite as a fallback.
   */
  onNLSSearch?: (params: NLSSearchTriggerParams) => void;
  /**
   * The current NLS base query string (from the latest search result).
   * When provided, this takes priority over llmMetadata?.payload?.query and
   * llmMetadata?.llmInfo?.queryRewrite in the apply callbacks, ensuring the NLS
   * call fires even if llmMetadata hasn't propagated to the MiniSearchBar prop yet.
   */
  currentQuery?: string;
}

/**
 * Parameters passed to onNLSSearch callback
 * Contains the updated search parameters that should trigger an NLS search
 */
export interface NLSSearchTriggerParams {
  /** The type of change that triggered the search */
  triggerType: 'dates' | 'rooms' | 'rates' | 'filters';
  /** Updated dates if changed */
  dates?: {
    checkin?: string;
    checkout?: string;
    flexible?: string;
    lengthOfStay?: number;
    numberOfNights?: number;
  };
  /** Updated rooms/guests if changed */
  roomsAndGuests?: {
    roomsCount: string;
    adultsCount: number;
    childrenCount?: number;
    childrenAges?: any;
  };
  /** Updated special rates if changed */
  specialRates?: {
    clusterCode: string;
    corporateCode?: string;
    groupCode?: string;
    specialRateCode?: string;
  };
  /** The base query string (from payload.query SSE field) to use for the NLS call */
  baseQueryRewrite?: string;
}

export interface MiniSearchBarSearchParams {
  dates?: {
    checkin?: string;
    checkout?: string;
    flexible?: string;
    lengthOfStay?: number;
    numberOfNights?: number;
  };
  roomsAndGuests?: {
    roomsCount: string;
    adultsCount: number;
    childrenCount?: number;
    childrenAges?: any;
  };
  specialRates?: {
    clusterCode: string;
    corporateCode?: string;
    groupCode?: string;
    specialRateCode?: string;
  };
}

/**
 * Default English labels for the NG molecules inside MiniSearchBar.
 * When no AEM model is provided (local dev / missing authoring), these
 * values ensure all stepper/radio labels are visible rather than blank
 * or "undefined".
 */
export const DEFAULT_MODEL: Record<string, any> = {
  // ── Datepicker ───────────────────────────────────────────────────────
  stayDates: 'Dates',
  dateModalHeader: 'Select your dates',
  dateHelperText: 'Check-in – Check-out',
  dateHelperTextMobile: 'Check-in – Check-out',
  nightLabel: 'night',
  nights: 'nights',
  // Tabs
  exactDatesTabText: 'Specific Dates',
  flexibleTabText: 'Flexible Dates',
  enableFlexibleSearch: true,
  flexibleDatesToggle: true,
  flexibleDateFormatLabel: 'MMMM YYYY',
  // CTAs
  submitSpecificDateCta: 'Apply',
  resetSpecificDateCta: 'Reset',
  flexibleDatesSubmitCta: 'Apply',
  submitSpecificDateCtaMobile: 'Select Dates',
  // Legacy keys (kept for backward compat)
  flexible: 'Flexible',
  exact: 'Exact',
  // Flexible dates section
  howManyNightsText: 'How many nights?',
  howManyNights: 'How many nights?',
  maxNumberOfNights: '9',
  // Month names for flexible dates calendar
  months: 'January,February,March,April,May,June,July,August,September,October,November,December',
  monthsShort: 'Jan,Feb,Mar,Apr,May,Jun,Jul,Aug,Sep,Oct,Nov,Dec',
  // Weekday names for calendar
  weekdays: 'S,M,T,W,T,F,S',
  weekdaysShort: 'Sun,Mon,Tue,Wed,Thu,Fri,Sat',
  weekdaysLong: 'Sunday,Monday,Tuesday,Wednesday,Thursday,Friday,Saturday',
  firstDayOfWeek: '0',
  thisWeekendLabel: 'This Weekend',
  yearSearchLabel: 'Year',

  // Rooms & Guests
  rooms: 'Rooms',
  room: 'Room',
  guests: 'Guests',
  guest: 'Guest',
  adults: 'Adults',
  adult: 'Adult',
  children: 'Children',
  roomsAndGuestEybrowText: 'Rooms & Guests',
  roomsAndGuestEyebrowText: 'Rooms & Guests',
  roomsAndGuestsDoneCta: 'Apply',
  roomsAndGuestsResetCta: 'Reset',
  roomsAndGuestAdultHelperText: '(Max: 8 total guests/room)',
  roomsAndGuestChildHelperText: '(Max: 8 total guests/room, Age-based rates may be available)',
  maxGuestPerRoom: '8',
  maxChildAgeLabel: '17',
  add: 'Add',
  remove: 'Remove',
  ageLabel: 'Age',
  smallerThanOneLabel: '<1',
  unitOfRoom: '',
  childAgeRequired: 'Child {x}: Age',
  roomListItems: [
    { roomQuantity: '1' },
    { roomQuantity: '2' },
    { roomQuantity: '3' },
    { roomQuantity: '4' },
    { roomQuantity: '5' },
    { roomQuantity: '6' },
    { roomQuantity: '7' },
    { roomQuantity: '8' },
    { roomQuantity: '9' },
  ],

  // Special Rates
  specialRatesEyebrowText: 'Rates & Points',
  specialRatesApplyCta: 'Apply',
  enterCodeEyebrowText: 'Enter Code',
  pointsAwardsTitle: 'Marriott Bonvoy® members:',
  disableUsePointCertificates: false,
  usePointsCertificates: 'Use Points/Awards',
  specialRatesListItems: [
    { specialRatesCode: 'none', specialRateslabel: 'Lowest Regular Rate' },
    { specialRatesCode: 'AAA', specialRateslabel: 'AAA/CAA' },
    { specialRatesCode: 'senior', specialRateslabel: 'Senior Discount' },
    { specialRatesCode: 'govt', specialRateslabel: 'Government & Military' },
    { specialRatesCode: 'corp', specialRateslabel: 'Corp/Promo Code' },
  ],
};
